import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPen, faPlus, faFileExport, faChartLine, faMinus } from '@fortawesome/free-solid-svg-icons';
import ColumnVisibilityModal from '../../../components/ColumnVisibilityModal';
import { Link } from 'react-router-dom';
import Table from '../../../components/Table';
import Pagination from '../../../components/Pagination';
import DespesaFilterBar from '../../../components/DespesaFilterBar';
import { fetchDespesas, fetchDespesasStats, fetchContasDespesa } from '../../../services/despesaService';
import { formatBRL } from '../../../utils/currency';

// Helpers locais
const formatDateLocal = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const datePart = value.split('T')[0].split(' ')[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      const [y, m, d] = datePart.split('-');
      return new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10)).toLocaleDateString('pt-BR');
    }
  }
  try {
    return new Date(value).toLocaleDateString('pt-BR');
  } catch (e) {
    return String(value);
  }
};

const renderConta = (value, contasMap = {}) => {
  if (!value && value !== 0) return '';
  if (typeof value === 'object') return value.descricao || value.label || value.nome || '';
  const str = String(value);
  // If value looks like an id and we have a map, return the mapped description
  if (/^\d+$/.test(str) && contasMap[str]) return contasMap[str];
  return str;
};

// columns are defined inside the component so they can access contasMap

const pageSizes = [5, 10, 20, 50];

const DespesasList = () => {
  // Definir colunas dentro do componente para usar contasMap no Cell de Conta
  const columns = [
    { Header: 'ID', accessor: 'id' },
    { Header: 'Data Balanço', accessor: 'dataBalanco', Cell: (value) => formatDateLocal(value) },
    { Header: 'Descrição', accessor: 'descricao' },
    { Header: 'Número NF', accessor: 'numeroNF' },
  { Header: 'Valor', accessor: 'valor', Cell: (value) => formatBRL(value) },
    { Header: 'Conta', accessor: 'conta', Cell: (value, row) => renderConta(value, contasMap) },
    { Header: 'Cadastrado Por', accessor: 'cadastradoPor' },
    { Header: 'Data Cadastro', accessor: 'dataInclusao', Cell: (value) => formatDateLocal(value) }
  ];
  const [filters, setFilters] = useState({ 
  dataBalancoInicio: '',
  dataBalancoFim: '',
    conta: '', 
    descricao: '',
    numeroNF: '',
    valorMin: '',
    valorMax: ''
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizes[1]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columns.map(col => col.accessor));
  const [despesas, setDespesas] = useState([]);
  const [totalPagesServer, setTotalPagesServer] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [stats, setStats] = useState(null);
  const [contasMap, setContasMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [appliedFilters, setAppliedFilters] = useState(filters);

  // Carregar dados (server-side pagination + filtros)
  useEffect(() => {
    const loadDespesas = async () => {
      try {
        setLoading(true);
        const params = {
          page,
          limit: pageSize,
          dataBalancoInicio: appliedFilters.dataBalancoInicio || undefined,
          dataBalancoFim: appliedFilters.dataBalancoFim || undefined,
          conta: appliedFilters.conta || undefined,
          descricao: appliedFilters.descricao || undefined,
          numeroNF: appliedFilters.numeroNF || undefined,
          valorMin: appliedFilters.valorMin || undefined,
          valorMax: appliedFilters.valorMax || undefined
        };

        const [statsData, despesasResult] = await Promise.all([
          fetchDespesasStats(),
          fetchDespesas(params)
        ]);

        // fetchDespesas now returns { despesas, pagination }
        setDespesas(despesasResult.despesas || []);
        const pag = despesasResult.pagination || {};
        setTotalPagesServer(pag.totalPages || Math.ceil((pag.total || 0) / (pag.limit || pageSize)));
        setTotalRecords(pag.total || 0);
        setStats(statsData);
        setError(null);
      } catch (err) {
        setError('Erro ao carregar despesas');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadDespesas();
  }, [page, pageSize, appliedFilters]);

  // Carregar mapa de contas (id -> descricao) para exibir descrição quando API retornar apenas id
  useEffect(() => {
    const loadContasMap = async () => {
      try {
        const contas = await fetchContasDespesa({ limit: 1000 });
        const map = {};
        (contas || []).forEach(c => {
          if (c && c.id != null) map[String(c.id)] = c.descricao || c.label || c.nome || `Conta ${c.id}`;
        });
        setContasMap(map);
      } catch (err) {
        console.error('Erro ao carregar contas para despesas:', err);
      }
    };

    loadContasMap();
  }, []);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setPage(1); // Reset page when filters change
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    setPage(1);
  };
  
  // Accept explicit filters (used when clearing from child)
  const handleApplyFiltersExplicit = (filtersToApply) => {
    if (filtersToApply && typeof filtersToApply === 'object') {
      setFilters(filtersToApply);
      setAppliedFilters(filtersToApply);
    } else {
      setAppliedFilters(filters);
    }
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      dataBalanco: '', 
      conta: '', 
      descricao: '',
      numeroNF: '',
      valorMin: '',
      valorMax: ''
    });
  };

  const handleColumnVisibilityChange = (columnAccessor) => {
    setVisibleColumns(prev => 
      prev.includes(columnAccessor) 
        ? prev.filter(col => col !== columnAccessor)
        : [...prev, columnAccessor]
    );
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  // Filtrar colunas visíveis
  const visibleColumnsData = columns.filter(col => visibleColumns.includes(col.accessor));

  // Server-side pagination: use despesas directly
  const paginatedData = despesas;
  const totalPages = totalPagesServer || Math.ceil(totalRecords / pageSize || 0);

  // Garantir que a página atual seja válida quando há mudanças nos filtros
  useEffect(() => {
    if (totalPages > 0 && page > totalPages) {
      setPage(1);
    }
  }, [totalPages, page]);

  // Preparar dados para a tabela com ações
  const tableData = paginatedData.map(despesa => ({
    ...despesa,
    actions: (
      <div className="flex space-x-2">
        <Link
          to={`/financeiro/despesas/${despesa.id}`}
          className="text-blue-600 hover:text-blue-800 transition-colors"
          title="Visualizar"
        >
          <FontAwesomeIcon icon={faEye} />
        </Link>
        <Link
          to={`/financeiro/despesas/${despesa.id}/edit`}
          className="text-green-600 hover:text-green-800 transition-colors"
          title="Editar"
        >
          <FontAwesomeIcon icon={faPen} />
        </Link>
      </div>
    )
  }));

  // Adicionar coluna de ações se não existir
  const columnsWithActions = visibleColumnsData.some(col => col.accessor === 'actions') 
    ? visibleColumnsData 
    : [...visibleColumnsData, { Header: 'Ações', accessor: 'actions' }];

  if (loading) {
    return (
      <div className="flex-1 w-full max-w-full overflow-x-hidden">
        <div className="px-2 sm:px-6">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 w-full max-w-full overflow-x-hidden">
      <div className="px-2 sm:px-6">
        <div className="max-w-full">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Despesas Financeiras</h2>
            <Link
              to="/financeiro/despesas/create"
              className="px-4 py-2 rounded bg-red-600 text-white font-semibold shadow hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-400 flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>Nova Despesa</span>
            </Link>
          </div>

          {/* Cards de Estatísticas */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-red-100 text-red-600">
                    <FontAwesomeIcon icon={faMinus} size="lg" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total de Despesas</p>
                    <p className="text-2xl font-bold text-red-600">
                      {formatBRL(stats.total)}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                    <FontAwesomeIcon icon={faFileExport} size="lg" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Quantidade de Lançamentos</p>
                    <p className="text-2xl font-bold text-blue-600">{stats.count}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-purple-100 text-purple-600">
                    <FontAwesomeIcon icon={faChartLine} size="lg" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Valor Médio</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {formatBRL(stats.average)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <ColumnVisibilityModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            columns={columns}
            visibleColumns={visibleColumns}
            onChange={handleColumnVisibilityChange}
          />

          <div className="max-w-full">
            <DespesaFilterBar 
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onClearFilters={handleClearFilters}
                onApplyFilters={handleApplyFiltersExplicit}
              />
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2 max-w-full">
            <div className="order-2 sm:order-1 w-full sm:w-auto">
              <Pagination 
                currentPage={page} 
                totalPages={totalPages} 
                onPageChange={handlePageChange} 
              />
              {totalRecords !== null && (
                <div className="text-sm text-gray-600 mt-1">Total: {totalRecords} registros</div>
              )}
            </div>
            <div className="order-1 sm:order-2 flex items-center w-full sm:w-auto justify-end gap-2">
              <label htmlFor="pageSize" className="text-sm text-gray-700 whitespace-nowrap mr-2">Por página:</label>
              <select
                id="pageSize"
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="px-2 py-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-400"
              >
                {pageSizes.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <button
                type="button"
                className="flex items-center gap-2 px-3 py-1 rounded bg-red-600 text-white font-medium shadow hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-400"
                onClick={() => setIsModalOpen(true)}
                aria-label="Exibir/Ocultar colunas"
              >
                <FontAwesomeIcon icon={faEye} />
                <span>Colunas</span>
              </button>
            </div>
          </div>

          <div className="max-w-full">
            <Table
              columns={columnsWithActions.filter(col => col.accessor === 'actions' || visibleColumns.includes(col.accessor))}
              data={tableData}
            />
          </div>

          {despesas.length === 0 && !loading && (
            <div className="text-center py-8 text-gray-500">
              Nenhuma despesa encontrada.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DespesasList;
